<template>
  <a-modal
    v-model="dialogVisible"
    title="编辑开票信息"
    :mask-closable="false"
    :body-style="{ paddingLeft: 0 }"
    @ok="confirm"
    @cancel="closeDialog"
  >
    <div class="body">
      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-suffix="："
        label-width="160px"
      >
        <el-form-item label="开票部门" prop="deptId">
          <department-tree-select
            ref="department"
            :dept-ids.sync="form.deptId"
            :company-ids.sync="form.companyId"
            :multiple="false"
            scenes="deptAndCompany"
            :z-index="2000"
          />
        </el-form-item>
        <el-form-item label="发票类型" prop="invoiceType">
          <el-radio-group v-model="form.invoiceType">
            <el-radio
              v-for="item in agencyInvoiceTypeDict.array"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="开票金额" prop="invoiceAmount">
          <el-input-number
            v-model="form.invoiceAmount"
            style="width: 200px"
            :min="0"
            :max="99999999999.99"
            :controls="false"
            :precision="2"
            placeholder="请输入"
          />
        </el-form-item>
        <el-form-item label="单位名称" prop="companyName">
          <el-input
            v-model.trim="form.companyName"
            disabled
            placeholder="请选择"
          />
        </el-form-item>
        <el-form-item label="统一社会信用代码" prop="socialCreditCode">
          <el-input
            v-model.trim="form.socialCreditCode"
            disabled
            placeholder="请选择"
          />
        </el-form-item>
        <el-form-item label="电话" prop="mobile">
          <el-input v-model.trim="form.mobile" placeholder="请填写" />
        </el-form-item>
        <el-form-item label="地址" prop="address">
          <el-input
            v-model.trim="form.address"
            placeholder="请填写"
            maxlength="50"
          />
        </el-form-item>
        <el-form-item label="开户行" prop="openBank">
          <el-input
            v-model.trim="form.openBank"
            placeholder="请填写"
            maxlength="200"
          />
        </el-form-item>
        <el-form-item label="账户" prop="account">
          <el-input
            v-model.trim="form.account"
            placeholder="请填写"
            maxlength="20"
          />
        </el-form-item>
      </el-form>
    </div>
  </a-modal>
</template>

<script>
// 组件
import DepartmentTreeSelect from '@/components/common/DepartmentTreeSelect.vue'
// 接口
import { getLatestIncomeApi } from '@/api/agency/service-fee'
// 工具
import { requiredRule } from '@/utils/form-validate'
import { cloneDeep } from 'lodash'
// 字典
import { agencyInvoiceTypeDict } from '@/dictionary/agency-service-fee'

/**
 * @description 服务费收取编辑开票信息弹窗
 */
export default {
  name: 'EditInvoiceDialog',

  components: { DepartmentTreeSelect },

  props: {
    visible: {
      type: Boolean,
      default: false
    },

    info: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    const checkDeptId = (rule, value, callback) => {
      if (this.form.deptId || this.form.companyId) {
        callback()
      } else {
        callback(new Error('请选择'))
      }
    }

    return {
      agencyInvoiceTypeDict,

      form: {
        // 部门id
        deptId: undefined,
        companyId: undefined,
        // 发票类型
        invoiceType: '',
        // 开票金额
        invoiceAmount: '',
        // 单位名称
        companyName: '',
        // 统一社会信用代码
        socialCreditCode: '',
        // 电话
        mobile: '',
        // 地址
        address: '',
        // 开户行
        openBank: '',
        // 账户
        account: ''
      },
      rules: {
        deptId: checkDeptId,
        invoiceType: requiredRule('请选择'),
        invoiceAmount: requiredRule('请填写'),
        companyName: requiredRule('请填写'),
        socialCreditCode: requiredRule('请填写'),
        mobile: {
          validator: (rule, value, callback) => {
            if (!/\d+$/.test(value)) {
              callback(new Error('请输入数字'))
            } else {
              callback()
            }
          },
          trigger: ['blur']
        }
      }
    }
  },

  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },

  watch: {
    visible(val) {
      if (val) {
        if (this.info.incomeId || this.info?.deptInfo?.deptId) {
          this.form = Object.assign({}, this.form, this.info)
        } else {
          this.getLatestIncome()
        }
      }
    }
  },

  methods: {
    confirm() {
      this.$refs.form.validate(valid => {
        if (!valid) return

        const deptName = this.form.deptId
          ? this.$refs.department.getLabelListByDeptIds(this.form.deptId)
          : []
        const companyName = this.form.companyId
          ? this.$refs.department.getLabelListByDeptIds(
            this.form.companyId,
            'company'
          )
          : []

        this.$emit(
          'confirm',
          Object.assign({}, this.form, {
            deptInfo: {
              deptId: this.form.deptId || this.form.companyId,
              name: deptName[0] || companyName[0] || ''
            }
          })
        )
        this.closeDialog()
      })
    },

    closeDialog() {
      this.$refs.form.clearValidate()
      this.$refs.form.resetFields()
      this.dialogVisible = false
    },

    /**
     * 获取上一条保存的数据
     */
    async getLatestIncome() {
      try {
        const { data } = await getLatestIncomeApi()
        this.form = {
          ...this.form,
          companyName: this.info.companyName,
          socialCreditCode: this.info.socialCreditCode,
          mobile: data?.mobile,
          address: data?.address,
          openBank: data?.openBank,
          account: data?.account
        }
      } catch (error) {
        this.$message.error(error.message)
      }
    }
  }
}
</script>

<style scoped></style>
